import { AssetRoute } from '@ps/utils/config'
import { getFileName, removeFileExtension } from '@com/utils/file'
import {
  executeAction,
  getAddSequenceAction,
  getSetSequenceActionAttrsAction,
} from '@ps/workbench/undo-redo'
import * as movieClipScript from '@ps/bridge/qc/scripts/movie-clip'
import _ from 'lodash'
import { getUpdateTargetAction } from '@ps/workbench/undo-redo'
import { projectBasicPath } from '@ps/workbench/config'
import store from '@ps/store'
import {
  addXlzToPlayable,
  productXlzDir,
  renameBeforeAddXlz,
} from '@ps/bridge/resource/xlz'
import { addSequenceFrameNode } from '@ps/bridge/qc/node/xlz'
import { readFileAsDataURL } from '@com/utils/file-reader'
import { getImageSize, loadImage } from '@com/image'
import iframe from '@ps/bridge/iframe/iframe'
import { wrapAddIvNode } from '@ps/store/interactive-video/brain/brain'

async function getFilesDataURL(fileList) {
  // 转换 DataURL
  const promiseList = fileList.map((file) => {
    const { raw } = file
    return readFileAsDataURL(raw)
  })
  const res = await Promise.all(promiseList)
  return res.map((item) => item.target.result)
}

export default {
  /**
   * @param fileList {{name: string, raw: File}[]}
   * @param projectKey {number}
   * @param actionName {string}
   * @returns {Promise<unknown>}
   */
  async addSequenceFrame(
    { rootState, state },
    { isPool, fileList, actionName, callback, isAddNode = true, targetNode },
  ) {
    const gameState = rootState.workspace.game

    let data = null
    let pathPrefix = null
    if (isPool) {
      const path = fileList[0]
      const fileName = getFileName(path)
      pathPrefix = path.replace(fileName, '').slice(0, -1) // 去掉首尾的斜杠
      // 资源池添加资源
      data = {
        fileList: fileList.map((path) => {
          return {
            name: getFileName(path),
          }
        }),
      }
    } else {
      pathPrefix = productXlzDir()
      // 转换 DataURL
      const dataUrlArr = await getFilesDataURL(fileList)
      const tempFileList = renameBeforeAddXlz(
        fileList.map((file, index) => {
          return {
            name: file.name,
            dataUrl: dataUrlArr[index],
          }
        }),
      )

      data = await addXlzToPlayable({
        fileList: tempFileList,
        pid: rootState.workspace.pid,
        pathPrefix,
      })
    }
    if (!data || !data.fileList) throw new Error('上传序列帧失败')
    if (!isAddNode) return

    const newFileList = data.fileList
    const scene =
      targetNode || rootState.workspace.game.sceneManager.currentSceneNode
    const { node, moveClipScript, binList } = addSequenceFrameNode({
      scene,
      pathPrefix,
      fileList: newFileList,
      actionName,
    })
    // 赋值当前 mv 的属性到 state
    state.data = moveClipScript.data
    state.defActionName = actionName
    moveClipScript.gotoAndPlay(actionName, state.playTime)
    // 获取序列帧的第一张图片宽高进行赋值
    if (fileList && fileList.length > 0) {
      let firstFrame = null
      if (isPool) {
        firstFrame = await loadImage(`${iframe.getAbsoluteUrl(fileList[0])}`)
      } else {
        firstFrame = await getImageSize(fileList[0].raw)
      }
      node.width = firstFrame.width
      node.height = firstFrame.height
    }

    const index = scene.getChildIndex(node)
    // 互动视频编辑器插入节点处理
    const ivRes = await wrapAddIvNode(scene, node, index)

    executeAction({
      action: getAddSequenceAction({
        node: ivRes ? ivRes.ivGroupNode : node,
        realNode: node,
        parent: scene,
        index: ivRes ? ivRes.index : index,
        activeUuid: gameState.activeNode ? gameState.activeNode.uuid : '',
      }),
      record: true,
    })
    typeof callback === 'function' && callback({ node, binList })
  },

  /**
   * 添加图片到序列帧上
   * 序列帧编辑面板继续本地上传图片到序列帧的全部图片,用action=allFramePlaySmart来存储
   * @param fileList 文件列表
   * @param projectKey
   * @param actionName 序列帧 action name，一个序列帧可能有多个 action
   * @param node 节点
   * @returns {Promise<void>}
   */
  async addImageToSequenceFrame(
    { rootState, state, dispatch },
    { fileList, projectKey, actionName, node, record, isFilter = false },
  ) {
    actionName = actionName || state.defActionName
    node = node || rootState.workspace.game.activeNode
    const mv = movieClipScript.getScript(node)
    const firstFilePath = mv.data.allFramePlaySmart.split(',')[0] // 序列帧中的第一个资源路径
    const uuid = firstFilePath.match(/xlz_([^/]*)\//)[1]
    const filePath = `${AssetRoute.GAME_ROOT}xlz_${uuid}`
    const dataUrlArr = await getFilesDataURL(
      fileList.map((file) => ({
        raw: file,
      })),
    )
    fileList = renameBeforeAddXlz(
      fileList.map((file, index) => {
        return {
          dataUrl: dataUrlArr[index],
          name: file.name,
        }
      }),
    )
    const res = await addXlzToPlayable({
      fileList,
      pid: Number(projectKey),
      pathPrefix: filePath,
    })
    if (!res) throw new Error('添加序列帧失败')

    // 为什么旧代码上要调用 assets.clear()，没道理啊，暂时先注释
    // const gameState = rootState.workspace.game
    // gameState.data.gameOb.assets.clear()

    const binList = fileList.map((item) => {
      return `${filePath}/${removeFileExtension(item.name)}.bin`
    })
    const newResourceList = binList.map((bin) => {
      return {
        value: bin,
        // 序列帧图片只支持 png
        src: `${projectBasicPath}${projectKey}/${bin.replace(
          /\.bin$/i,
          '.png',
        )}`,
      }
    })
    const oldResourceList = _.get(
      rootState.workspace.editorConfig.nodeConfig,
      `${node.uuid}.resource`,
    )
    if (oldResourceList) {
      let resourceList = []
      if (!isFilter) {
        resourceList = [...newResourceList, ...oldResourceList]
      } else {
        resourceList = oldResourceList
          .map((item, index) => ({
            ...item,
            value: binList[index],
          }))
          .filter((v) => Boolean(v.value))
      }

      store.dispatch('workspace/editorConfig/updateTargetNodeConfig', {
        originalResource: resourceList,
        resource: resourceList,
        node,
      })
    }

    // 当前当前图片列表
    const currentActionBinList = [
      ...(mv.data[actionName] || '').split(','),
      ...binList,
    ].filter((bin) => Boolean(bin))

    executeAction({
      action: getUpdateTargetAction({
        newValue: [
          {
            node,
            actionName,
            images: currentActionBinList.join(','),
            allImages: _.uniq(
              [
                ...(mv.data.allFramePlaySmart || '').split(','),
                ...binList,
              ].filter((bin) => Boolean(bin)),
            ).join(','),
          },
        ],
        oldValue: [
          {
            node,
            actionName,
            images: mv.data[state.defActionName],
            allImages: mv.data.allFramePlaySmart,
          },
        ],
      }),
      record,
    })
  },
  async toggleSequenceFrame(
    { rootState, state, dispatch },
    { fileList, projectKey, node, record, isPool = false },
  ) {
    node = Array.isArray(node) ? node : [rootState.workspace.game.activeNode]
    let binList = []
    if (isPool) {
      binList = fileList
    } else {
      let pathPrefix = null
      pathPrefix = productXlzDir()
      // 转换 DataURL
      const dataUrlArr = await getFilesDataURL(fileList)
      const tempFileList = renameBeforeAddXlz(
        fileList.map((file, index) => {
          return {
            name: file.name,
            dataUrl: dataUrlArr[index],
          }
        }),
      )
      await addXlzToPlayable({
        fileList: tempFileList,
        pid: rootState.workspace.pid,
        pathPrefix,
      })
      binList = tempFileList.map((item) => {
        return `${pathPrefix}/${removeFileExtension(item.name)}.bin`
      })
    }
    const taskList = node.map(async (node) => {
      const mv = movieClipScript.getScript(node)
      const actionName = mv.defActionName
      return {
        node,
        actionName,
        images: binList.join(','),
        oImages: mv.data.allFramePlaySmart,
      }
    })
    const res = await Promise.all(taskList)
    const newValueArr = []
    const oldValueArr = []
    res.forEach((item) => {
      newValueArr.push({
        node: item.node,
        actionName: item.actionName,
        images: item.images,
        allImages: item.images,
      })
      oldValueArr.push({
        node: item.node,
        actionName: item.actionName,
        images: item.oImages,
        allImages: item.oImages,
      })
    })

    await executeAction({
      action: getUpdateTargetAction({
        newValue: newValueArr,
        oldValue: oldValueArr,
      }),
      record,
    })
  },
  async removeImageFromSequenceFrame(
    { rootState, state, dispatch },
    { actionName, node, images, record, recordNodeConfig = true },
  ) {
    actionName = actionName || state.defActionName
    node = node || rootState.workspace.game.activeNode
    const mv = movieClipScript.getScript(node)

    const currentActionBinList = [
      ...(mv.data[actionName] || '').split(','),
    ].filter((bin) => Boolean(bin))

    // 至少要有一张图
    if (currentActionBinList.length <= 1) {
      return
    }

    // const leftActionBinList = currentActionBinList.filter(bin => images.some(j => bin !== j))
    const leftActionBinList = currentActionBinList.filter(
      (bin) => !images.includes(bin),
    )
    const oldResourceList = _.get(
      rootState.workspace.editorConfig.nodeConfig,
      `${node.uuid}.resource`,
    )
    if (oldResourceList) {
      const resourceList = oldResourceList.filter((item) =>
        leftActionBinList.includes(item.value),
      )
      store.dispatch('workspace/editorConfig/updateTargetNodeConfig', {
        originalResource: resourceList,
        resource: resourceList,
        node,
      })
    }
    executeAction({
      action: getUpdateTargetAction({
        newValue: [
          {
            node,
            actionName,
            images: leftActionBinList.join(','),
            allImages: _.uniq(
              [...(mv.data.allFramePlaySmart || '').split(',')].filter(
                (bin) => {
                  return Boolean(bin) && images.some((j) => bin !== j)
                },
              ),
            ).join(','),
          },
        ],
        oldValue: [
          {
            node,
            actionName,
            images: mv.data[state.defActionName],
            allImages: mv.data.allFramePlaySmart,
          },
        ],
      }),
      record,
    })
  },
  async replaceImageFromSequenceFrame(
    { rootState, state, dispatch },
    { fileList, projectKey, images, node },
  ) {
    await dispatch('addImageToSequenceFrame', {
      fileList,
      projectKey,
      node,
      actionName: node ? node.MovieClip.defActionName : '',
      record: false,
      isFilter: true,
    })
    await dispatch('removeImageFromSequenceFrame', {
      images,
      node,
      actionName: node ? node.MovieClip.defActionName : '',
      record: false,
    })
  },
  /**
   * 更新序列帧属性
   * @param {number} interval 序列帧播放间隔
   * @param {number} playTime 播放次数
   * @param {boolean} autoPlay 入场自动播放
   * @param {boolean} keepLastFrame 保持尾帧
   * @param {boolean} record
   */
  setSequenceAttrs(
    { state },
    { interval, playTime, autoPlay, keepLastFrame, record = true },
  ) {
    executeAction({
      action: getSetSequenceActionAttrsAction({
        newValue: {
          interval: !_.isUndefined(interval) ? interval : state.interval,
          playTime: !_.isUndefined(playTime) ? playTime : state.playTime,
          autoPlay: !_.isUndefined(autoPlay) ? autoPlay : state.autoPlay,
          keepLastFrame: !_.isUndefined(keepLastFrame)
            ? keepLastFrame
            : state.keepLastFrame,
        },
        oldValue: {
          interval: state.interval,
          playTime: state.playTime,
          autoPlay: state.autoPlay,
          keepLastFrame: state.keepLastFrame,
        },
      }),
      record,
    })
  },

  /**
   * 设置序列帧某个action的图片
   * @param actionName {string}
   * @param images {string}
   * @param allImages {string}
   * @param record {boolean}
   */
  updateTargetAction(
    { rootState, state },
    { actionName, images, node, allImages, record = true },
  ) {
    const gameState = rootState.workspace.game
    node = node || gameState.activeNode
    const mv = movieClipScript.getScript(node)
    executeAction({
      action: getUpdateTargetAction({
        newValue: [
          {
            actionName,
            images,
            node,
            allImages: allImages || mv.data.allFramePlaySmart,
          },
        ],
        oldValue: [
          {
            actionName,
            images: mv.data[state.defActionName],
            node,
            allImages: mv.data.allFramePlaySmart,
          },
        ],
      }),
      record,
    })
  },
}
